import {useModel} from "./useModel"
import linkify from "linkify-it"
import {useHistory} from "react-router-dom"
import {useEffect, useRef} from "react"

const playNotificationSound = () => {
    try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.value = 800
        oscillator.type = "sine"

        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.5)
    } catch (error) {
        console.warn("Could not play notification sound:", error)
    }
}

export default function useNotification() {
    const model = useModel()
    const history = useHistory()
    const originalTitleRef = useRef<string>(document.title)
    const titleBlinkIntervalRef = useRef<NodeJS.Timeout | null>(null)
    const isBlinkingRef = useRef<boolean>(false)

    useEffect(() => {
        const updateOriginalTitle = () => {
            const currentTitle = document.title
            if (!isBlinkingRef.current && !currentTitle.startsWith("New Message - ")) {
                originalTitleRef.current = currentTitle.replace(/^\(.*?\)\s*/, "")
            }
        }

        updateOriginalTitle()

        const titleElement = document.querySelector("title")
        if (titleElement) {
            const observer = new MutationObserver(() => {
                updateOriginalTitle()
            })
            observer.observe(titleElement, {
                childList: true,
                subtree: true,
                characterData: true
            })

            const titleCheckInterval = setInterval(() => {
                updateOriginalTitle()
            }, 1000)

            const handleFocus = () => {
                if (titleBlinkIntervalRef.current) {
                    clearInterval(titleBlinkIntervalRef.current)
                    titleBlinkIntervalRef.current = null
                    isBlinkingRef.current = false
                }
                updateOriginalTitle()
                document.title = originalTitleRef.current
            }

            window.addEventListener("focus", handleFocus)

            return () => {
                observer.disconnect()
                clearInterval(titleCheckInterval)
                window.removeEventListener("focus", handleFocus)
                if (titleBlinkIntervalRef.current) {
                    clearInterval(titleBlinkIntervalRef.current)
                }
            }
        }
    }, [])

    const updateTitleForNewMessage = () => {
        if (!document.hasFocus()) {
            const currentTitle = document.title
            if (!currentTitle.startsWith("New Message - ")) {
                originalTitleRef.current = currentTitle.replace(/^\(.*?\)\s*/, "")
            }
            const baseTitle = originalTitleRef.current.replace(/^\(.*?\)\s*/, "")

            if (titleBlinkIntervalRef.current) {
                clearInterval(titleBlinkIntervalRef.current)
            }

            let isBlinking = false
            isBlinkingRef.current = true
            titleBlinkIntervalRef.current = setInterval(() => {
                if (document.hasFocus()) {
                    if (titleBlinkIntervalRef.current) {
                        clearInterval(titleBlinkIntervalRef.current)
                        titleBlinkIntervalRef.current = null
                    }
                    isBlinkingRef.current = false
                    const currentTitle = document.title.replace(/^New Message - /, "").replace(/^\(.*?\)\s*/, "")
                    originalTitleRef.current = currentTitle
                    document.title = currentTitle
                    return
                }
                document.title = isBlinking ? baseTitle : `New Message - ${baseTitle}`
                isBlinking = !isBlinking
            }, 1000)
        }
    }

    const checkPermission = () => {
        if (!("Notification" in window) || !("permission" in Notification)) {
            return false
        }
        if (Notification.permission === "denied" || Notification.permission === "default") {
            return false
        }
        return true
    }

    const newMessageNotification = async (message) => {
        if (model.user.id !== message.fromUserId && checkPermission()) {
            let payload = message.content
            if ((message.files || []).length > 0 && !payload) {
                payload = "Sent you a file"
            }

            const user = await model.getOneUser(message.fromUserId)
            const defaultAvatar = `${window.location.origin}/image/DefaultAvatar.png`

            const {notification} = message
            if (notification?.sound === true) {
                playNotificationSound()
            }
            if (notification?.web === true) {
                updateTitleForNewMessage()
            }

            const notificationObj = new Notification(user?.fullName, {
                body: payload,
                tag: message.messageId,
                icon: user.photo ? user.photo["64"] : defaultAvatar
            })
            notificationObj.onclick = () => {
                const urls = linkify().match(message.payload)
                if (urls) {
                    const {url} = urls[0]
                    const origin = window.location.origin
                    if (url.startsWith(origin)) {
                        window.location.href = url
                        return
                    }
                }
                history.push(`/communication?room=${message.roomId}`)
            }
        }
    }

    return {newMessageNotification}
}
